import { createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment';

export const fetchStatusData = createAsyncThunk<
	FetchStatusDataResult,
	FetchStatusDataPayload,
	{ rejectValue: string }
>(
	'detectionStatus/fetchStatusData',
	async (
		{
			api,
			accountId,
		},
		{ rejectWithValue },
	) => {
		try {
			const startDate = moment().subtract(1, 'days').toDate();
			const endDate = moment().toDate();

			const results: FetchStatusDataResult = {
				activeDetections: { count: 0, status: 'failed' },
				scansRemediated: { count: 0, status: 'failed' },
				scansFinished: { count: 0, status: 'failed' },
				activePolicies: { count: 0, totalCount: 0, status: 'failed' },
				scansInProgress: { count: 0, status: 'failed' },
			};

			// Fetch all data in parallel
			const requests = [
				// Active Detections
				async () => {
					try {
						const scansActive = await api.getAccountESFeeds(
							accountId,
							startDate,
							endDate,
							'',
							'',
							[],
							1,
							25,
							'',
							'desc',
							'Open',
						);
						results.activeDetections = {
							count: scansActive.totalCount,
							status: 'success',
						};
					} catch {
						results.activeDetections = { count: 0, status: 'failed' };
					}
				},
				// Scans Remediated
				async () => {
					try {
						const scansRemediated = await api.getAccountESFeeds(
							accountId,
							startDate,
							endDate,
							'',
							'',
							[],
							1,
							25,
							'',
							'desc',
							'Dismissed',
						);
						results.scansRemediated = {
							count: scansRemediated.totalCount,
							status: 'success',
						};
					} catch {
						results.scansRemediated = { count: 0, status: 'failed' };
					}
				},
				// Scans Finished
				async () => {
					try {
						const scansFinished = await api.getAccountESScans(
							accountId,
							startDate,
							endDate,
							'',
							'',
							[],
							1,
							25,
							'',
							'desc',
						);
						results.scansFinished = {
							count: scansFinished.totalCount,
							status: 'success',
						};
					} catch {
						results.scansFinished = { count: 0, status: 'failed' };
					}
				},
				// Active Policies
				async () => {
					try {
						const activePolicies = await api.getPolicyEngineFeeds(
							accountId,
							'',
						);

						const activeCount = activePolicies.data.reduce(
							(acc, curr) => {
								if (!curr.disabled) {
									acc += 1;
								}
								return acc;
							},
							0,
						);

						results.activePolicies = {
							count: activeCount,
							totalCount: activePolicies.count,
							status: 'success',
						};
					} catch {
						results.activePolicies = {
							count: 0,
							totalCount: 0,
							status: 'failed',
						};
					}
				},
				// Scans In Progress
				async () => {
					try {
						const scansInProgressCount = await api.getScansinProgress(
							accountId,
						);

						results.scansInProgress = {
							count: scansInProgressCount,
							status: 'success',
						};
					} catch {
						results.scansInProgress = { count: 0, status: 'failed' };
					}
				},
			];

			await Promise.all(requests.map((req) => req()));

			return results;
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: 'An unexpected error occurred while fetching status data';
			return rejectWithValue(message);
		}
	},
);

type FetchStatusDataPayload = {
	api: any;
	accountId: string;
};

type FetchStatusDataResult = {
	activeDetections: { count: number; status: 'success' | 'failed' };
	scansRemediated: { count: number; status: 'success' | 'failed' };
	scansFinished: { count: number; status: 'success' | 'failed' };
	activePolicies: {
		count: number;
		totalCount: number;
		status: 'success' | 'failed';
	};
	scansInProgress: { count: number; status: 'success' | 'failed' };
};
